




































































export type TDatas = {
  propulsivePhase: TSidePhases;
  brakingPhase: TSidePhases;
  heelStrike: TAngles;
  flatFoot: TAngles;
  toesOff: TAngles;
};
export type TSidePhases = {
  left: TPhases;
  right: TPhases;
};
export type TPhases = {
  abs: {
    value: number;
    indicators: TPhaseIndicators;
  };
  rel: {
    value: number;
    indicators: TPhaseIndicators;
  };
};
export type TPhaseIndicators = {
  class: string;
  background: string;
  text: string;
  chevron: string | null;
};
export type TAngles = {
  left: {
    abs: TAngle;
    rel: TAngle;
  };
  right: {
    abs: TAngle;
    rel: TAngle;
  };
};
export type TAngle = {
  avg: number;
  stdMin: number;
  stdMax: number;
  range: ERange;
};

export enum ERange {
  VeryLow = 'very_low',
  Low = 'low',
  Neutral = 'neutral',
  High = 'high',
  VeryHigh = 'very_high'
}

export enum EIndicator {
  Bad = 'bad',
  Warning = 'warning',
  good = 'good'
}
// Import vendors ----------------------------------------------------------------------------------
import {
  defineComponent,
  ref,
  computed,
  watchEffect,
  onMounted,
  onUnmounted,
  ComputedRef,
  Ref
} from '@vue/composition-api';
// Import plugins ----------------------------------------------------------------------------------
import i18n from '@/plugins/i18n';
import { DrawAnglesChartSVG } from '@/plugins/charts/prosup-chart/angles-prosup-running';
// Import utils ----------------------------------------------------------------------------------
import { chartColors } from '@/utils/charts-colors';
// Import helper -----------------------------------------------------------------------------------
import { staticValues } from '@/helpers/static-values.helper';
// -------------------------------------------------------------------------------------------------

export default defineComponent({
  name: 'ChartRunningPronationSupinationAnglesAdvanced',
  props: {
    aggregates: {
      type: Object,
      required: true
    },
    relative: {
      type: Boolean,
      required: true,
      default: false
    },
    height: {
      type: Number,
      default: 450
    }
  },
  setup(properties) {
    window.addEventListener('resize', handleResize);

    const rCanvas: Ref<HTMLCanvasElement | null> = ref(null);
    const rCanvasTooltip: Ref<HTMLCanvasElement | null> = ref(null);
    const rCanvasContent: Ref<HTMLCanvasElement | null> = ref(null);

    const parentWidth = ref(700);
    const width = ref(700);
    const { runningAngleAverage } = staticValues();

    const data: ComputedRef<TDatas> = computed(() => {
      const data: any = {
        propulsivePhase: {},
        brakingPhase: {},
        heelStrike: {},
        flatFoot: {},
        toesOff: {}
      };

      for (const phase of [{ propulsivePhase: 'propulsion' }, { brakingPhase: 'loading' }]) {
        data[Object.keys(phase)[0]] = {
          left: {
            abs: {
              value: properties.aggregates[`running_${Object.values(phase)[0]}_time`].left_foot.avg,
              indicators: getPhaseIndicators(
                properties.aggregates[`running_${Object.values(phase)[0]}_time`].left_foot.range
              )
            },
            rel: {
              value:
                properties.aggregates[`running_proportion_${Object.values(phase)[0]}_time`].left_foot.avg,
              indicators: getPhaseIndicators(
                properties.aggregates[`running_proportion_${Object.values(phase)[0]}_time`].left_foot.range
              )
            }
          },
          right: {
            abs: {
              value: properties.aggregates[`running_${Object.values(phase)[0]}_time`].right_foot.avg,
              indicators: getPhaseIndicators(
                properties.aggregates[`running_${Object.values(phase)[0]}_time`].right_foot.range
              )
            },
            rel: {
              value:
                properties.aggregates[`running_proportion_${Object.values(phase)[0]}_time`].right_foot.avg,
              indicators: getPhaseIndicators(
                properties.aggregates[`running_proportion_${Object.values(phase)[0]}_time`].right_foot.range
              )
            }
          }
        };
      }

      for (const angle of [{ heelStrike: 'foot_in' }, { flatFoot: 'mid' }, { toesOff: 'foot_out' }]) {
        data[Object.keys(angle)[0]] = {
          left: {
            abs: {
              avg: properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].left_foot.avg,
              stdMin:
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].left_foot.avg -
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].left_foot.std,
              stdMax:
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].left_foot.avg +
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].left_foot.std,
              range:
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].left_foot.range
            },
            rel: {
              avg: properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                .left_foot.avg,
              stdMin:
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`].left_foot
                  .avg -
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`].left_foot
                  .std,
              stdMax:
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`].left_foot
                  .avg +
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`].left_foot
                  .std,
              range:
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`].left_foot
                  .range
            }
          },
          right: {
            abs: {
              avg: properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].right_foot.avg,
              stdMin:
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].right_foot.avg -
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].right_foot.std,
              stdMax:
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].right_foot.avg +
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].right_foot.std,
              range:
                properties.aggregates[`running_pronation_angle_${Object.values(angle)[0]}`].right_foot.range
            },
            rel: {
              avg: properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                .right_foot.avg,
              stdMin:
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                  .right_foot.avg -
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                  .right_foot.std,
              stdMax:
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                  .right_foot.avg +
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                  .right_foot.std,
              range:
                properties.aggregates[`running_relative_pronation_angle_${Object.values(angle)[0]}`]
                  .right_foot.range
            }
          }
        };
      }

      return data;
    });
    const dataMode = computed(() => (properties.relative ? 'rel' : 'abs'));
    const responsiveData = computed(() => {
      return [
        {
          angle: {
            id: 'toesOff',
            name: i18n.t('commons.standards.run.toe-off'),
            left: data.value.toesOff.left,
            right: data.value.toesOff.right
          },
          timePhase: {
            id: 'digitigrade',
            icon: require('@/assets/images/propulsion.svg'),
            left: data.value.propulsivePhase.left,
            right: data.value.propulsivePhase.right
          }
        },
        {
          angle: {
            id: 'flatFoot',
            name: i18n.t('commons.standards.run.flat-foot'),
            left: data.value.flatFoot.left,
            right: data.value.flatFoot.right
          },
          timePhase: {
            id: 'taligrade',
            icon: require('@/assets/images/heel-strike.svg'),
            left: data.value.brakingPhase.left,
            right: data.value.brakingPhase.right
          }
        },
        {
          angle: {
            id: 'heelStrike',
            name: i18n.t('commons.standards.run.heel-strike'),
            left: data.value.heelStrike.left,
            right: data.value.heelStrike.right
          },
          timePhase: null
        }
      ];
    });

    function getAngleRange(range: ERange) {
      switch (range) {
        case ERange.Low || ERange.VeryLow:
          return 'low-range';
        case ERange.High || ERange.VeryHigh:
          return 'high-range';
        case ERange.Neutral:
          return 'good-range';
        default:
          return 'good-range';
      }
    }

    function getPhaseIndicators(range: ERange): TPhaseIndicators {
      switch (range) {
        case ERange.Neutral:
          return {
            class: 'good-range',
            background: chartColors.good.background,
            text: chartColors.good.fill,
            chevron: null
          };
        case ERange.Low:
          return {
            class: 'low-range warning-range',
            background: chartColors.warning.background,
            text: chartColors.warning.fill,
            chevron: 'down'
          };
        case ERange.High:
          return {
            class: 'high-range warning-range',
            background: chartColors.warning.background,
            text: chartColors.warning.fill,
            chevron: 'up'
          };
        case ERange.VeryLow:
          return {
            class: 'low-range bad-range',
            background: chartColors.bad.background,
            text: chartColors.bad.fill,
            chevron: 'down'
          };
        case ERange.VeryHigh:
          return {
            class: 'high-range bad-range',
            background: chartColors.bad.background,
            text: chartColors.bad.fill,
            chevron: 'up'
          };
        default: {
          return {
            class: 'good-range',
            background: chartColors.good.background,
            text: chartColors.good.fill,
            chevron: null
          };
        }
      }
    }

    function handleResize() {
      const parent: any = rCanvasContent.value;
      parentWidth.value = parent.clientWidth;
      width.value = window.innerWidth;
    }

    function draw() {
      if (rCanvas.value) {
        const graphCanvas = new DrawAnglesChartSVG(
          rCanvas.value as HTMLCanvasElement,
          rCanvasTooltip.value as HTMLCanvasElement,
          parentWidth.value,
          data.value,
          runningAngleAverage,
          dataMode.value,
          properties.height
        );
        requestAnimationFrame(() => {
          graphCanvas.draw();
        });
      }
    }

    watchEffect(() => {
      draw();
    });

    onMounted(() => handleResize());
    onUnmounted(() => window.removeEventListener('resize', handleResize));

    return {
      // Values
      parentWidth,
      width,
      rCanvas,
      dataMode,
      rCanvasContent,
      rCanvasTooltip,
      responsiveData,
      // Methods
      getAngleRange
    };
  }
});
